import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  AriOriginalRequestParams,
  AriRoomRateActivityParams,
  AriRoomRateStateParams,
  getAriOriginalRequest,
  getAriRoomRateActivity,
  getAriRoomRateState,
} from '~/services/ReportingService';

export const ariRoomRateLookupQueryKeys = {
  all: ['ari', 'roomRateLookup'] as const,
  state: (params?: AriRoomRateStateParams) => [...ariRoomRateLookupQueryKeys.all, 'state', params ?? {}] as const,
  activity: (params?: AriRoomRateActivityParams) =>
    [...ariRoomRateLookupQueryKeys.all, 'activity', params ?? {}] as const,
  originalRequest: (params?: AriOriginalRequestParams) =>
    [...ariRoomRateLookupQueryKeys.all, 'originalRequest', params ?? {}] as const,
};

function hasIdentifier(params?: { roomRateId?: string; roomTypeId?: string; ratePlanId?: string }) {
  return !!(params?.roomRateId || params?.roomTypeId || params?.ratePlanId);
}

export function useAriRoomRateState(params?: AriRoomRateStateParams) {
  return useQuery({
    queryKey: ariRoomRateLookupQueryKeys.state(params),
    queryFn: ({ signal }) => getAriRoomRateState(params, { signal }),
    // The API 400s without an identifier or a stay date.
    enabled: hasIdentifier(params) && !!params?.stayDate,
    placeholderData: keepPreviousData,
    // Every attempt is a billed BigQuery job and these failures do not clear on
    // retry, so don't pay for the same error three times.
    retry: false,
  });
}

export function useAriRoomRateActivity(params?: AriRoomRateActivityParams) {
  return useQuery({
    queryKey: ariRoomRateLookupQueryKeys.activity(params),
    queryFn: ({ signal }) => getAriRoomRateActivity(params, { signal }),
    enabled: hasIdentifier(params) && !!params?.stayDate,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

// staleTime: Infinity because an ari_logs payload is immutable once written,
// and can be a few hundred KB.
export function useAriOriginalRequest(params?: AriOriginalRequestParams | null) {
  return useQuery({
    queryKey: ariRoomRateLookupQueryKeys.originalRequest(params ?? undefined),
    queryFn: ({ signal }) => getAriOriginalRequest(params, { signal }),
    enabled: !!params?.ariLogId && !!params?.sourceService && !!params?.createdAt,
    staleTime: Infinity,
    retry: false,
  });
}
